import React, { useEffect, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import moment from 'moment-timezone';
import CustomTimePicker from './CustomTimePicker';

const TimezoneDatePickerWithTime = ({ value, onChange, timezone, timeOnly = false, ...rest }) => {
  const [displayValue, setDisplayValue] = useState(null);

  const { inputRef: callerInputRef, ...restWithoutInputRef } = rest;

  /**
   * Convert TZ date → browser-local Date for the picker.
   * Wall-clock values in the target timezone are used as-is so the
   * displayed time matches what the user expects for that timezone.
   */
  const toDisplay = (date) => {
    if (!date) return null;

    const m = moment.tz(date, timezone);

    if (timeOnly) {
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth(), today.getDate(), m.hours(), m.minutes(), m.seconds(), m.milliseconds());
    }

    return new Date(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second(), m.millisecond());
  };

  /**
   * Convert picker-produced browser-local Date → TZ-aware Date.
   */
  const fromDisplay = (date) => {
    if (!date) return null;

    if (timeOnly) {
      const todayInTz = moment.tz(timezone);
      return moment
        .tz(
          {
            year: todayInTz.year(),
            month: todayInTz.month(),
            date: todayInTz.date(),
            hour: date.getHours(),
            minute: date.getMinutes(),
            second: date.getSeconds(),
            millisecond: date.getMilliseconds(),
          },
          timezone
        )
        .toDate();
    }

    return moment
      .tz(
        {
          year: date.getFullYear(),
          month: date.getMonth(),
          date: date.getDate(),
          hour: date.getHours(),
          minute: date.getMinutes(),
          second: date.getSeconds(),
          millisecond: date.getMilliseconds(),
        },
        timezone
      )
      .toDate();
  };

  // Sync incoming value → display Date
  useEffect(() => {
    if (!value) {
      setDisplayValue(null);
      return;
    }

    if (Array.isArray(value)) {
      setDisplayValue(value.map(toDisplay));
    } else {
      setDisplayValue(toDisplay(value));
    }
  }, [value, timezone, timeOnly]);

  const handleChange = (e) => {
    const picked = e.value;

    if (!picked) {
      onChange({ ...e, value: null, target: { ...e.target, value: null } });
      return;
    }

    let convertedValue;
    if (Array.isArray(picked)) {
      convertedValue = picked.map(fromDisplay);
    } else {
      convertedValue = fromDisplay(picked);
    }

    onChange({
      ...e,
      value: convertedValue,
      target: { ...e.target, value: convertedValue },
    });
  };

  // ── timeOnly → custom component (full control over snapping + 24hr input) ──

  if (timeOnly) {
    return (
      <CustomTimePicker
        value={displayValue}
        onChange={handleChange}
        id={restWithoutInputRef.id}
        placeholder={restWithoutInputRef.placeholder}
        className={restWithoutInputRef.className}
        inputRef={callerInputRef}
        disabled={restWithoutInputRef.disabled}
      />
    );
  }

  // ── date / date+time → standard PrimeReact Calendar (unchanged) ───────────

  return <Calendar value={displayValue} onChange={handleChange} timeOnly={false} {...restWithoutInputRef} inputRef={callerInputRef} />;
};

export default TimezoneDatePickerWithTime;
